/* eslint-disable @typescript-eslint/no-explicit-any */
import { Undo } from '@material-ui/icons';
import { MaterialTableProps } from 'material-table';
import React, { Suspense, useEffect } from 'react';
import { connect } from 'react-redux';
import LayoutForm from '../../../../components/Layout/LayoutForm';
import ActionsMenu from '../../../../components/Layout/Menus/ActionsMenu/ActionsMenu';
import TableWithEdit from '../../../../components/Tables/TableWithEdit';
import Title from '../../../../components/Title/Title';
import { permissionType } from '../../../../constants/permissions_definitions';
import { exportToExcel } from '../../../../helpers/exportToXslx';
import { formatNumber } from '../../../../helpers/formatNumber';
import { tFormKey, tKey } from '../../../../helpers/translate';
import { getPendingToSendInvoicesAction, rollbackSentInvoiceAction } from '../../../../redux/accounting/actions';
import { InvoiceListItem } from '../../../../redux/accounting/definitions';
import { selectLoading, selectPendingInvoicesList } from '../../../../redux/accounting/selectors';
import { selectUserCan } from '../../../../redux/permissions/selectors';
import { AppState } from '../../../../redux/root-reducer';
import StateCode from '../../../../types/Invoices/StateCode';
import AccountingSubmenu from '../../AccountingSubmenu';
import useRollbackInvoiceModal from '../hooks/useRollbackInvoiceModal';

const TransitionModal = React.lazy(() => import('../../../../components/Modal/TransitionModal'));
export interface PendingInvoicesListProps {
  loading: boolean;
  pendingInvoicesList: InvoiceListItem[];
  getPendingInvoices: () => void;
  rollbackSentInvoice: (invoice_id: number, stateCode: StateCode) => void;
  userCan: (permission: permissionType) => boolean | undefined;
}

const PendingInvoicesList: React.FC<PendingInvoicesListProps> = ({
  loading,
  pendingInvoicesList,
  userCan,
  getPendingInvoices,
  rollbackSentInvoice,
}) => {
  const { rollabackModal, openRollbackModal, closeRollbackModal } = useRollbackInvoiceModal();
  const canPay = userCan('allow_send_invoice_pending_to_sage');

  useEffect(() => {
    getPendingInvoices();
  }, [getPendingInvoices]);

  const actionIcons = {
    GoBack: () => <Undo style={{ color: 'rgba(0, 0, 0, 0.54)' }} />,
  };

  const actions = [
    (rowData: InvoiceListItem) => ({
      icon: actionIcons.GoBack,
      onClick: () => {
        openRollbackModal(rowData);
      },
      tooltip: tKey('Retroceder'),
      hidden: !canPay,
    }),
  ];

  const tableData: MaterialTableProps<InvoiceListItem> = {
    title: '',
    columns: [
      {
        field: 'reference_number',
        title: tKey('Factura'),
        //@ts-ignore
        width: '200px',
      },
      {
        field: 'due_date',
        title: tKey('Vencimiento'),
        //@ts-ignore
        width: '120px',
      },
      {
        field: 'total_amount',
        title: tKey('Importe'),
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'right',
        },
        render: (rowData: InvoiceListItem) => (
          <div style={{ textAlign: 'right' }}>{formatNumber({ value: rowData.total_amount }) + ' €'}</div>
        ),
        //@ts-ignore
        width: '120px',
      },
      {
        field: 'description',
        title: tKey('Comentario'),
        //@ts-ignore
        //width: '35%',
      },
      {
        field: 'center_cost',
        title: tKey('Centro de coste'),
        //@ts-ignore
        width: '180px',
      },
    ],
    data: pendingInvoicesList,
    options: {
      exportButton: true,
      exportCsv: (columns, data) => {
        exportToExcel(columns, data, tKey(`Facturas pendientes de envío`));
      },
      pageSize: 100,
      pageSizeOptions: [20, 50, 100],
      tableLayout: 'fixed',
    },
    actions,
  };

  return (
    <LayoutForm
      rightSubmenu={<ActionsMenu />}
      leftSubmenu={<AccountingSubmenu selected="pending_invoices" submenu={tKey('Facturas servicios')} />}
    >
      <Title>{tKey('Facturas pendientes de envío')}</Title>

      <TableWithEdit
        tableData={{ ...tableData, isLoading: loading }}
        key={JSON.stringify(tableData.data)}
        permission={canPay}
      />
      <Suspense fallback={<></>}>
        <TransitionModal
          view={rollabackModal.view}
          handleClose={() => {
            closeRollbackModal();
          }}
          bodyModal={`${tFormKey('rollback.invoice.modal.pre')} ${rollabackModal.reference_number} ${tFormKey(
            'rollback.pending.invoice.modal.post',
          )}`}
          buttonOk={tKey('Sí')}
          buttonKo={tKey('No')}
          title={`${tFormKey('Retroceder')}: ${rollabackModal.reference_number} - ${rollabackModal.entity_name}`}
          handleYes={() => {
            rollbackSentInvoice(rollabackModal.invoice_id, rollabackModal.stateCode);
            closeRollbackModal();
          }}
        />
      </Suspense>
    </LayoutForm>
  );
};
const mapStateProps = (state: AppState) => ({
  userCan: selectUserCan(state),
  loading: selectLoading(state),
  pendingInvoicesList: selectPendingInvoicesList(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  getPendingInvoices: (): void => dispatch(getPendingToSendInvoicesAction()),
  rollbackSentInvoice: (invoice_id: number, stateCode: StateCode) =>
    dispatch(rollbackSentInvoiceAction(invoice_id, stateCode)),
});

export default connect(mapStateProps, mapDispatchToProps)(PendingInvoicesList);
